import React, { useState, useEffect } from "react";
import { SubClassAdminRequestDTO } from "Types/Admin/AdminSubClassType";
import { useGetAllCategoriesQuery } from "Services/Admin/CategoryAdminApiSlice";
import { CategoryAdminDTO } from "Types/Admin/AdminCategoryType";
import { useGetAdminSubClassesQuery } from "Services/Admin/SubClassApiSlice";

interface SubClassFormProps {
  initialValues: SubClassAdminRequestDTO;
  onSubmit: (data: SubClassAdminRequestDTO) => void;
  onCancel: () => void;
  mode: "add" | "edit" | "view";
  isSubmitting?: boolean;
}

export const SubClassForm: React.FC<SubClassFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  mode,
  isSubmitting: parentSubmitting = false,
}) => {

  const [isSubmitting, setIsSubmitting] = useState(false);

  
  const { 
    data: categories = [], 
    isLoading: isLoadingCategories 
  } = useGetAllCategoriesQuery();
  
  // Get existing subclasses to check for duplicate codes
  const { data: subClassesData } = useGetAdminSubClassesQuery();
  const existingSubClasses = subClassesData?.results || [];
  
  const [isIdManuallyEdited, setIsIdManuallyEdited] = useState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  
  // For add mode, create a new object WITHOUT subClassCode
  const getInitialFormData = (): SubClassAdminRequestDTO => {
    if (mode === "add") {
      return {
        subClassName: initialValues.subClassName || "",
        categoryCode: initialValues.categoryCode || "",
        isActive: initialValues.isActive || "Y",
      };
    }
    return {
      subClassCode: initialValues.subClassCode || "",
      subClassName: initialValues.subClassName || "",
      categoryCode: initialValues.categoryCode || "",
      isActive: initialValues.isActive || "Y",
    };
  };

  const [formData, setFormData] = useState<SubClassAdminRequestDTO>(
    getInitialFormData()
  );

  // Fetch existing sub class codes to avoid duplicates
  const fetchExistingSubClassCodes = async (): Promise<string[]> => {
    try {
      return existingSubClasses.map(sc => sc.subClassCode);
    } catch (error) {
      console.error("Failed to fetch existing sub class codes:", error);
      return [];
    }
  };

  // Generate a unique subClassCode
  const generateUniqueCode = async (baseName: string): Promise<string> => {
    const existingCodes = await fetchExistingSubClassCodes();
    let baseCode = baseName
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "_")
      .substring(0, 15);
    
    if (!baseCode) baseCode = "SUB";
    
    let counter = 1;
    let newCode = baseCode;
    
    while (existingCodes.includes(newCode)) {
      newCode = `${baseCode}_${counter}`;
      counter++;
    }
    
    return newCode;
  };

  // Generate ID only in add mode and when not manually edited
  useEffect(() => {
    if (mode === "add" && !isIdManuallyEdited && formData.subClassName) {
      const generateId = async () => {
        setIsGeneratingCode(true);
        setCodeError(null);
        try {
          const newId = await generateUniqueCode(formData.subClassName || "");
          setFormData((prev) => ({
            ...prev,
            subClassCode: newId,
          }));
        } catch (error) {
          setCodeError("Failed to generate code");
          console.error("Code generation failed:", error);
        } finally {
          setIsGeneratingCode(false);
        }
      };
      generateId();
    }
  }, [mode, isIdManuallyEdited, formData.subClassName]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "subClassName" && mode === "add" && !isIdManuallyEdited) {
      setFormData((prev) => ({
        ...prev,
        subClassName: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubClassCodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setIsIdManuallyEdited(value !== "");
    setCodeError(null);
    
    if (value) {
      const existingCodes = await fetchExistingSubClassCodes();
      if (existingCodes.includes(value)) {
        setCodeError("This Sub Category Code already exists");
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      ...(mode === "add" && { subClassCode: value }),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent duplicate submission logic exactly like CategoryForm
    if (isSubmitting || parentSubmitting) return;

    if (codeError) {
      alert(codeError);
      return;
    }
    
    try {
      setIsSubmitting(true);
      // Wait for onSubmit to complete before releasing the local lock
      await onSubmit(formData);
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isViewMode = mode === "view";
  // Combined loading state for UI
  const anySubmitting = isSubmitting || parentSubmitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-white rounded-lg">
      {(mode !== "add" || isIdManuallyEdited) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sub Category Code {mode === "add" ? "(Optional)" : "*"}
          </label>
          <input
            type="text"
            name="subClassCode"
            value={formData.subClassCode || ""}
            onChange={handleSubClassCodeChange}
            disabled={isViewMode || mode === "edit"}
            readOnly={mode === "edit"}
            className={`w-full px-3 py-2 border rounded-md ${
              mode === "edit" ? "bg-gray-50" : ""
            } ${codeError ? "border-red-500" : ""}`}
            placeholder={mode === "add" ? "Enter custom code or leave empty to auto-generate" : ""}
          />
          {mode === "add" && (
            <>
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to auto-generate a unique code from the Sub Category Name
              </p>
              {isGeneratingCode && (
                <p className="text-xs text-blue-500 mt-1">Generating code...</p>
              )}
              {codeError && (
                <p className="text-xs text-red-500 mt-1">{codeError}</p>
              )}
            </>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Sub Category Name*
        </label>
        <input
          type="text"
          name="subClassName"
          value={formData.subClassName || ""}
          onChange={handleChange}
          disabled={isViewMode}
          required
          className="w-full px-3 py-2 border rounded-md"
        />
      </div>
     {/* {mode !== "add" && (
  <div>
    <label className="block text-sm font-medium text-gray-500 mb-1">
      Category Code
    </label>
    <input
      type="text"
      value={formData.categoryCode || ""}
      disabled
      readOnly
      className="w-full px-3 py-2 border rounded-md text-sm bg-gray-100 border-gray-300"
    />
    <p className="text-xs text-gray-500 mt-1">
      Category code is system-generated and cannot be modified
    </p>
  </div>
)} */}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Category*
        </label>
        <select
          name="categoryCode"
          value={formData.categoryCode || ""}
          onChange={handleChange}
          disabled={isViewMode || isLoadingCategories}
          required
          className="w-full px-3 py-2 border rounded-md"
        >
          <option value="">Select Category</option>
          {categories.map((category: CategoryAdminDTO) => (
            <option key={category.categoryCode} value={category.categoryCode}>
              {category.categoryName}
            </option>
          ))}
        </select>
        {isLoadingCategories && (
          <p className="text-xs text-gray-500 mt-1">Loading categories...</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Status*
        </label>
        <select
          name="isActive"
          value={formData.isActive || "Y"}
          onChange={handleChange}
          disabled={isViewMode}
          required
          className={`w-full px-3 py-2 border rounded-md ${
            isViewMode ? "bg-gray-50" : ""
          }`}
        >
          <option value="Y">Active</option>
          <option value="N">Inactive</option>
        </select>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={anySubmitting}
          className="px-4 py-2 border rounded-md hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        {!isViewMode && (
          <button
            type="submit"
            disabled={!!codeError || anySubmitting}
            className={`px-4 py-2 rounded-md flex items-center justify-center gap-2
              ${
                anySubmitting
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600"
              } text-white transition-all duration-200`}
          >
            {anySubmitting && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
            )}

            {anySubmitting
              ? mode === "add"
                ? "Creating..."
                : "Updating..."
              : mode === "add"
              ? "Create"
              : "Update"}
          </button>
        )}
      </div>
    </form>
  );
};